import { forwardRef } from 'react';

import {
  Drawer,
  DrawerHeader,
  DrawerOverlayProvider,
  DrawerProps,
  useDrawerInstance,
} from '@cast/design-system';

import { useUpdateEdgeLocationMutation } from 'hooks/mutations/omni';

import { EdgeLocationDetailsForm } from '../../_components/EdgeLocationDetailsForm';
import { formModelAddonsToApiAddons } from '../../_utils';
import { EdgeLocationFormValues, EnhancedEdgeLocation } from '../../types';

export type EditEdgeLocationDrawerProps = DrawerProps & {
  edgeLocation: EnhancedEdgeLocation;
  clusterId: string;
};

export const EditEdgeLocationDrawer = forwardRef<
  HTMLDivElement,
  EditEdgeLocationDrawerProps
>(({ edgeLocation, clusterId, ...props }, ref) => {
  const { close } = useDrawerInstance();

  const { mutate: updateEdgeLocation, isPending } =
    useUpdateEdgeLocationMutation({
      edgeLocationId: edgeLocation.id!,
      clusterId,
      onSuccess: close,
    });

  const handleSubmit = (values: EdgeLocationFormValues) => {
    updateEdgeLocation({
      description: values.description ?? '', // Allow removing the description text
      edgeClusterSpec: {
        addons: formModelAddonsToApiAddons(values.addons),
      },
    });
  };

  return (
    <Drawer
      ref={ref}
      testId="edit-edge-location-drawer"
      size="medium"
      {...props}
    >
      <DrawerOverlayProvider>
        <DrawerHeader />

        <EdgeLocationDetailsForm
          mode="edit"
          edgeLocation={edgeLocation}
          onSubmit={handleSubmit}
          isLoading={isPending}
          onClose={() => close()}
        />
      </DrawerOverlayProvider>
    </Drawer>
  );
});

EditEdgeLocationDrawer.displayName = 'EditEdgeLocationDrawer';
